<template>
  <div class="bg-white">
    <div v-if="isMfaView && singleMethodAvail && !showAuthenticationDlg">
      <p class="text-center font-weight-bold mfa-title mt-4">We're Sorry!</p>
      <p class="text-center">
        You don't have any other MFA methods. Contact
        <span class="primary--text text-decoration-underline cursor-pointer" @click="showContactUsModal = true">
          {{ $t("fields.supportOnviv") }}
        </span>
        for assistance
      </p>
      <p class="font-weight-bold text-center">{{ $t("messages.mfaSingleMethodDesc") }}</p>
    </div>
    <p v-else-if="isMfaView && !showAuthenticationDlg" class="font-weight-bold mfa-title mt-4">
      {{ !hasMfaMethods ? $t("messages.setupMFATitle") : $t("messages.selectMFADesc") }}
    </p>
    <div v-if="changeAuthenticationDlgToWrapper">
      <authentication-methods-dlg
        :hasMfaMethods="hasMfaMethods"
        :mfaMethod="mfaMethod"
        :value="showAuthenticationDlg"
        @dismiss="showAuthenticationDlg = false"
        @mfa-verified="handleMfaVerified"
      ></authentication-methods-dlg>
    </div>
    <div v-else :class="{ 'ps-2 pe-3': isMfaView }">
      <div v-if="isMfaView && !hasMfaMethods">
        <p class="text-body-2">{{ $t("messages.setupMFADesc") }}</p>
        <p class="text-body-2">{{ $t("messages.confirmMfaMethod") }}</p>
        <p class="font-weight-bold secondary-title">{{ $t("messages.preferredMfaMethod") }}</p>
      </div>
      <div v-else-if="!isMfaView">
        <p class="font-weight-bold">
          {{$t("messages.mfaDescription")}}
          <span class="primary--text font-weight-regular text-decoration-underline cursor-pointer" @click="showContactUsModal = true">
            {{$t("fields.supportOnviv")}}.
          </span>
        </p>
      </div>
      <div v-if="showSmsMethod" :class="{ 'cursor-pointer': !isSmsAvailable || isMfaView }" @click="showSmsAuthDlg()">
        <o-sheet class="d-flex mb-4" :class="{ 'border--primary border-weight--bold': isSmsAvailable && !isMfaView }">
          <v-icon color="primary" class="me-4">$sms</v-icon>
          <v-icon v-if="isSmsAvailable" color="successGreen" class="verified-icon" small>$verifiedCheck</v-icon>
          <div>
            <h4 class="primary--text">{{ $t("fields.sms") }}</h4>
            <p class="mb-0 text-body-2">{{ $t("fields.mfaSmsDesc") }}</p>
            <p class="mb-0 text-caption">{{ $t("fields.mfaSmsCaption") }}</p>
          </div>
        </o-sheet>
      </div>
      <div v-if="showAppMethod" :class="{ 'cursor-pointer': !isAppAvailable || isMfaView }" @click="showAppAuthDlg()">
        <o-sheet class="d-flex mb-4" :class="{ 'border--primary border-weight--bold': isAppAvailable && !isMfaView }">
          <v-icon color="primary" class="me-4">$authenticator</v-icon>
          <v-icon v-if="isAppAvailable" color="successGreen" class="verified-icon" small>$verifiedCheck</v-icon>
          <div>
            <h4 class="primary--text">{{ $t("fields.app") }}</h4>
            <p class="mb-0 text-body-2">{{ $t("fields.mfaAppDesc") }}</p>
          </div>
        </o-sheet>
      </div>
      <div v-if="showEmailMethod" :class="{ 'cursor-pointer': isMfaView }" @click="showEmailAuthDlg()">
        <o-sheet class="d-flex mb-4" :class="{ 'border--primary border-weight--bold': !isMfaView }">
          <v-icon color="primary" class="me-4">$emailIcon</v-icon>
          <v-icon color="successGreen" class="verified-icon" small>$verifiedCheck</v-icon>
          <div>
            <h4 class="primary--text">{{ $t("fields.email") }}</h4>
            <p class="mb-0 text-body-2">{{ $t("fields.mfaEmailDesc") }}</p>
          </div>
        </o-sheet>
      </div>
      <authentication-methods-dlg
        v-if="showAuthenticationDlg"
        :mfaMethod="mfaMethod"
        :value="showAuthenticationDlg"
        @dismiss="showAuthenticationDlg = false"
        @mfa-verified="handleMfaVerified"
      ></authentication-methods-dlg>
    </div>
    <!-- Redirect to login -->
    <o-sheet
      v-if="isMfaView && !showAuthenticationDlg"
      class="d-flex justify-center align-center mb4 mt-6"
      style="background-color: transparent; box-shadow: none;"
    >
      <a class="primary--text text-decoration-underline" @click.prevent="returnToLogin">
        Return to Login
      </a>
    </o-sheet>
    <p v-if="isMfaView && !showAuthenticationDlg && !singleMethodAvail" class="mt-6 text-center">
      Contact
      <span class="primary--text text-decoration-underline cursor-pointer" @click="showContactUsModal = true">
        {{ $t("fields.supportOnviv") }}</span>
      if you should need assistance.
    </p>
    <contact-us-dlg v-model="showContactUsModal" @dimiss-contact-us="showContactUsModal = false"></contact-us-dlg>
  </div>

</template>
<script lang="ts">
import Vue from "vue";
import OSheet from "@/framework/OSheet.vue";
import AuthenticationMethodsDlg from "../dialog/Authentication/AuthenticationMethodsDlg.vue";
import ContactUsDlg from "../dialog/ContactUsDlg.vue";
import { Themes, defaultTheme } from "@/store/theme/themes";
import { mfaTypeEnum } from "@/service";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapGetters, mapActions } = createHelpers<RootStoreInterface>();
const actions = mapActions(["resetCredentials","initialize", "updateMFAVerification"]);
const getters = mapGetters(["getMfaAvail", "getMfaUsed", "getMfaComplete"]);


export default Vue.extend({
  components: {
    OSheet,
    AuthenticationMethodsDlg,
    ContactUsDlg,
  },
  data: () => ({
    isVerified: false,
    showAuthenticationDlg: false,
    mfaMethod: "" as mfaTypeEnum,
    showContactUsModal: false,
  }),
  props: {
    // for MFA view after login
    isMfaView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    hasMfaMethods(): boolean {
      return this.getMfaAvail.length > 0;
    },
    isSmsAvailable(): boolean {
      return this.getMfaAvail.includes(mfaTypeEnum.SMS);
    },
    isAppAvailable(): boolean {
      return this.getMfaAvail.includes(mfaTypeEnum.AUTH_APP);
    },
    isEmailAvailable(): boolean {
      return this.getMfaAvail.includes(mfaTypeEnum.EMAIL);
    },
    // Change the authentication dialog to a wrapper component
    changeAuthenticationDlgToWrapper(): boolean {
      return this.showAuthenticationDlg && this.isMfaView;
    },
    singleMethodAvail(): boolean {
      return this.getMfaAvail.length == 1;
    },
    mfaAuthWithMethods(): boolean {
      return this.hasMfaMethods && this.isMfaView;
    },
    showSmsMethod(): boolean {
      return (this.mfaAuthWithMethods && this.hasMfaMethods && this.isSmsAvailable || !this.mfaAuthWithMethods);
    },
    showAppMethod(): boolean {
      return (this.mfaAuthWithMethods && this.hasMfaMethods && this.isAppAvailable || !this.mfaAuthWithMethods);
    },
    showEmailMethod(): boolean {
      return (this.mfaAuthWithMethods && this.hasMfaMethods && this.isEmailAvailable || !this.mfaAuthWithMethods);
    },
    isMFAComnpleted(): boolean {
      return this.getMfaComplete;
    }
  },
  methods: {
    ...actions,
    showSmsAuthDlg() {
      if (this.isSmsAvailable && !this.isMfaView) return;

      this.mfaMethod = mfaTypeEnum.SMS;
      this.showAuthenticationDlg = true;

      this.$emit('clear-reset-form');
    },
    showAppAuthDlg() {
      if (this.isAppAvailable && !this.isMfaView) return;

      this.mfaMethod = mfaTypeEnum.AUTH_APP;
      this.showAuthenticationDlg = true;

      this.$emit('clear-reset-form');
    },
    showEmailAuthDlg() {
      if(!this.isMfaView) return;
      
      if (this.isEmailAvailable && !this.isMfaView) return;

      this.mfaMethod = mfaTypeEnum.EMAIL;
      this.showAuthenticationDlg = true;
    },
    showParentMethodscomponent() {
      this.showAuthenticationDlg = false;
    },
    async returnToLogin() {
      try {
        await this.resetCredentials();
        await this.initialize();
        await this.$router.replace({ name: "Login" });
      } catch (e) {
        console.error("Return to login failed", e);
      }
    },
    async handleMfaVerified(rawResult: string) {
      const result = String(rawResult || "").trim().toLowerCase();

      const isMfaVerified =
        result === "success" ||
        result === "tenantselectionrequired" ||
        result.startsWith("theme:");
      if (!isMfaVerified) return;
      await (this as any).updateMFAVerification(true);
      (this as any).$store?.commit?.("setMfaComplete", true);

      if (result.startsWith("theme:")) {
        const themeName = result.replace("theme:", "").trim();

        try {
          localStorage.removeItem("tenantSelectionRequired");

          if ((this as any).$store?._mutations?.["setTenantSelectionRequired"]) {
            (this as any).$store.commit("setTenantSelectionRequired", false);
          } else if ((this as any).$store?._mutations?.["auth/setTenantSelectionRequired"]) {
            (this as any).$store.commit("auth/setTenantSelectionRequired", false);
          }

          if (Themes[themeName]) {
            localStorage.setItem("activeTheme", themeName);

            const store = (this as any).$store;
            if (store?._actions?.["applyTheme"]) {
              await store.dispatch("applyTheme", themeName);
            } else if (store?._actions?.["theme/applyTheme"]) {
              await store.dispatch("theme/applyTheme", themeName);
            }
          }
        } catch (e) {
          console.warn("Could not apply theme", e);
        }

        this.showAuthenticationDlg = false;
        this.$router.replace("/").catch(() => {});
        return;
      }
      if (result === "success") {
        try {
          localStorage.removeItem("tenantSelectionRequired");

          if ((this as any).$store?._mutations?.["setTenantSelectionRequired"]) {
            (this as any).$store.commit("setTenantSelectionRequired", false);
          } else if ((this as any).$store?._mutations?.["auth/setTenantSelectionRequired"]) {
            (this as any).$store.commit("auth/setTenantSelectionRequired", false);
          }
        } catch {}

        this.showAuthenticationDlg = false;
        this.$router.replace("/").catch(() => {});
        return;
      }
      try {
        localStorage.setItem("tenantSelectionRequired", "true");
        if ((this as any).$store?._mutations?.["setTenantSelectionRequired"]) {
          (this as any).$store.commit("setTenantSelectionRequired", true);
        }
      } catch (e) {
        console.warn("Could not persist tenantSelectionRequired flag", e);
      }
      // tenantSselectionRequired - More than 1 tenant
      const next = (this.$route.query.next as string | undefined) || "Home";
      this.$router.replace({ name: "ChangeTenant", query: { next } }).catch(() => {});
    }
  },
  created() {
    this.$eventBus.$on("hideAppBarBtn", this.showParentMethodscomponent);
  },
  mounted() {
    // open Authentication component with used MFA
    if (this.getMfaUsed && this.isMfaView && !this.isMFAComnpleted) {
      this.mfaMethod = this.getMfaUsed as mfaTypeEnum;
      this.showAuthenticationDlg = true;
    }
  },
  destroyed() {
    this.$eventBus.$off("hideAppBarBtn", this.showParentMethodscomponent);
    this.showAuthenticationDlg = false;

  },
});
</script>
<style lang="scss" scoped>
.mfa-title {
  font-size: 22px;
}

.secondary-title {
  font-size: 18px;
}

.cursor-pointer {
  cursor: pointer;
}

:deep(.v-sheet) {
  position: relative;
}

.verified-icon {
  position: absolute;
  top: 16px;
  right: 16px;
}

.bg-white {
  background-color: rgba(255, 255, 255, 0.75);
  border-radius: 10px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
